import React from "react";
import {withTranslation, WithTranslation} from "react-i18next";
import MetricsUtil, {ActionName} from "src/utils/MetricsUtil";
import {KatButton, KatDropdown, KatIcon, KatInputGroup, KatLabel, KatOption} from "@amzn/katal-react";
import {
    countryDefaultCategoryTRexIdMap,
    dimensionUnitAbbrMap,
    getCountryListConfig,
    getCurrencySymbolConfig,
    getCurrencyUnitConfig,
    getDimensionUnitAbbrConfig,
    getDimensionUnitListConfig,
    getWeightUnitAbbrConfig,
    getWeightUnitConfig
} from "src/config/Config";
import NumberKatInput from "src/components/common/NumberKatInput";
import ErrorStatusComponent from "src/components/common/ErrorStatusComponent";
import {bindActionCreators, Dispatch} from "redux";
import {connect} from "react-redux";
import {setDefinedProductUpdate} from "src/actions/QueryProductUpdateAction";
import QueryProductInfo from "src/model/QueryProduct";
import {GetCurrentCountryCodeResponse, GetIsSelectNew, GetProductDataResponse} from "src/model/ProductMatch";
import ApplicationState from "src/model/ApplicationState";
import RequestStatus from "src/model/RequestStatus";
import GetProgramFeesResponse from "src/model/ProgramFees";
import ConvertUtil from "src/utils/ConvertUtil";
import GetFeeCategoryListResponse from "src/model/FeeCategoryList";
import {setFeeCatQueryStart} from "src/actions/FeeCategoryQueryAction";
import LoadingStatusComponent from "src/components/common/LoadingStatusComponent";


interface StateProps {
    currentCountryCodeRes: GetCurrentCountryCodeResponse,
    programFeesRes: GetProgramFeesResponse,
    productRes: GetProductDataResponse,
    isSelectNewRes: GetIsSelectNew,
    feeCatRes: GetFeeCategoryListResponse,
}

interface OwnProps {
    readonly currentCountryCode: string,
    readonly metricsUtil?: MetricsUtil,
    handleCountryChange: (countryCode: string) => void
}

interface DispatchProps {
    readonly dispatchQueryProduct: (countryCode: string, product: QueryProductInfo) => void,
    readonly dispatchQueryFeeCats: (countryCode: string, locale: string) => void,
}

const mapStateToProps = (state: ApplicationState): StateProps => ({
    currentCountryCodeRes: state.currentCountryCodeRes,
    programFeesRes: state.programFeesRes,
    productRes: state.productRes,
    isSelectNewRes: state.isSelectNewRes,
    feeCatRes: state.feeCatRes,
});

const mapDispatchToProps = (dispatch: Dispatch) => (
    bindActionCreators({
        dispatchQueryProduct: setDefinedProductUpdate,
        dispatchQueryFeeCats: setFeeCatQueryStart,
    }, dispatch)
);

type Props = StateProps & OwnProps & DispatchProps & WithTranslation;

interface State {
    dimensionUnitAbbr: string,
    tRexId: number,
    length: number,
    width: number,
    height: number,
    weight: number,
    price: number,
    shippingCharge: number,
    hasEstimated: boolean,
    isValid: boolean,
}

export class NewAsinEstimationComponent extends React.Component<Props, State> {

    protected readonly metricsUtil: MetricsUtil;

    constructor(props: Props) {
        super(props);

        const product = props.isSelectNewRes.oldProduct;
        const countryCode = this.props.currentCountryCode;
        let dimensionUnitAbbr = getDimensionUnitAbbrConfig(countryCode);
        if (product != null && product.dimensionUnit == "inches" && dimensionUnitAbbr != "in") {
            dimensionUnitAbbr = "in";
        } else if (product != null && product.dimensionUnit == "centimeters" && dimensionUnitAbbr != "cm") {
            dimensionUnitAbbr = "cm";
        }

        this.state = {
            dimensionUnitAbbr: dimensionUnitAbbr,
            tRexId: -1,
            weight: (product != null && product.weight != null && product.weight > 0) ? ConvertUtil.weightConvert(product.weight, product.weightUnit, getWeightUnitAbbrConfig(countryCode)) : -1,
            length: (product != null && product.length != null && product.length > 0) ? product.length : -1,
            width: (product != null && product.width != null && product.width > 0) ? product.width : -1,
            height: (product != null && product.height != null && product.height > 0) ? product.height : -1,
            price: -1,
            shippingCharge: -1,
            hasEstimated: false,
            isValid: true
        };

        this.handleChangeCountry = this.handleChangeCountry.bind(this);
        this.handleChangeDimensionUnit = this.handleChangeDimensionUnit.bind(this);
        this.handleChangeCategory = this.handleChangeCategory.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);

        if (this.props.metricsUtil) {
            this.metricsUtil = this.props.metricsUtil;
        } else {
            this.metricsUtil = MetricsUtil.getDefaultMetricUtil();
        }
    }

    componentDidMount() {
        let currentCountryCode: string = this.props.currentCountryCode;
        if (this.props.currentCountryCode == "") { /* For revcal public only */
            currentCountryCode = getCountryListConfig()[0];
        }
        this.updateCategoryTRexIdMapConfig(currentCountryCode);
    }

    handleChangeCountry(event: KatDropdown.ChangeEvent) {
        const previousCountryCode = this.props.currentCountryCode;
        this.props.handleCountryChange(event.detail.value);
        this.setState({
            tRexId: -1,
            weight: ConvertUtil.weightConvert(this.state.weight, getWeightUnitAbbrConfig(previousCountryCode), getWeightUnitAbbrConfig(event.detail.value))
        });
        this.updateCategoryTRexIdMapConfig(event.detail.value);
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("newProduct_countryCode", "select");
        }
    }

    updateCategoryTRexIdMapConfig(countryCode: string) {
        this.props.dispatchQueryFeeCats(countryCode, this.props.i18n.language);
    }

    handleChangeDimensionUnit(event: KatDropdown.ChangeEvent) {
        this.setState({dimensionUnitAbbr: event.detail.value});
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("dimensionUnit", "select");
        }
    }

    handleChangeCategory(event: KatDropdown.ChangeEvent) {
        this.setState({tRexId: Number(event.detail.value), isValid: true});
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("category", "select");
        }
    }

    handleSubmit(event: React.FormEvent) {
        event.preventDefault();
        const countryCode = this.props.currentCountryCode;
        const dimensionUnitAbbr = this.state.dimensionUnitAbbr;
        if (!countryCode || !dimensionUnitAbbr || this.state.tRexId < 0 || this.state.weight < 0 || this.state.length < 0 || this.state.width < 0 || this.state.height < 0
            || this.state.price < 0 || this.state.shippingCharge < 0) {
            this.setState({isValid: false});
            return;
        }
        let product: QueryProductInfo = {
            tRexId: this.state.tRexId + '',
            weightUnit: getWeightUnitConfig(countryCode),
            packageWeight: this.state.weight + '',
            dimensionUnit: dimensionUnitAbbrMap[dimensionUnitAbbr],
            packageLength: this.state.length + '',
            packageWidth: this.state.width + '',
            packageHeight: this.state.height + '',
            afnPriceStr: this.state.price + '',
            mfnPriceStr: this.state.price + '',
            mfnShippingPriceStr: this.state.shippingCharge + '',
            currency: getCurrencyUnitConfig(countryCode),
            isNewDefined: true,
        };
        this.setState({hasEstimated: true, dimensionUnitAbbr: dimensionUnitAbbr});
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("estimateButton", "click");
            this.metricsUtil.setLongTimer(ActionName.TIME_ON_PAGE_AFTER_PRODUCT_DEFINITION);
            this.metricsUtil.setShortTimer(ActionName.TIME_ON_ONE_PRODUCT);
        }
        this.props.dispatchQueryProduct(countryCode, product);
    }

    productDimensions(dimensionUnitAbbr: string) {
        const {t} = this.props;
        return (
            <div className="input-group-container">
                <KatLabel text={t('SC_FBA_Calc_package_dimensions')}/>
                <div className="input-group-box">
                    <KatInputGroup>
                        <NumberKatInput isDisabled={false} className="input-dimension" minValue={0} isFloat={true}
                                        isMandatory={true}
                                        value={this.state.length > 0 ? this.state.length : undefined}
                                        onInput={(input: number, hasError: boolean): void => {
                                            if (!hasError) {
                                                this.setState({length: input, isValid: true});
                                            }
                                        }}/>
                        <KatLabel text={dimensionUnitAbbr}/>
                    </KatInputGroup> X <KatInputGroup>
                    <NumberKatInput isDisabled={false} className="input-dimension" minValue={0} isFloat={true}
                                    isMandatory={true}
                                    value={this.state.width > 0 ? this.state.width : undefined}
                                    onInput={(input: number, hasError: boolean): void => {
                                        if (!hasError) {
                                            this.setState({width: input, isValid: true});
                                        }
                                    }}/>
                    <KatLabel text={dimensionUnitAbbr}/>
                </KatInputGroup> X <KatInputGroup>
                    <NumberKatInput isDisabled={false} className="input-dimension" minValue={0} isFloat={true}
                                    isMandatory={true}
                                    value={this.state.height > 0 ? this.state.height : undefined}
                                    onInput={(input: number, hasError: boolean): void => {
                                        if (!hasError) {
                                            this.setState({height: input, isValid: true});
                                        }
                                    }}/>
                    <KatLabel text={dimensionUnitAbbr}/>
                </KatInputGroup>
                </div>
            </div>
        );
    }

    productWeight() {
        const {t} = this.props;
        const countryCode = this.props.currentCountryCode;
        const weightUnitAbbr = getWeightUnitAbbrConfig(countryCode);
        return (
            <div className="input-group-container">
                <KatLabel text={t('SC_FBA_Calc_productInfo_shipWeight')}/>
                <div className="input-group-box">
                    <KatInputGroup>
                        <NumberKatInput isDisabled={false} className="input-weight" minValue={0} isFloat={true}
                                        isMandatory={true}
                                        value={this.state.weight > 0 ? this.state.weight : undefined}
                                        onInput={(input: number, hasError: boolean): void => {
                                            if (!hasError) {
                                                this.setState({weight: input, isValid: true});
                                            }
                                        }}/>
                        <KatLabel text={weightUnitAbbr}/>
                    </KatInputGroup>
                </div>
            </div>
        );
    }

    productPrice(displayStringId: string) {
        const {t} = this.props;
        const currencyUnit = getCurrencySymbolConfig(this.props.currentCountryCode);
        return (
            <div className="input-group-container">
                <KatLabel text={t(displayStringId)}/>
                <div className="input-group-box">
                    <KatInputGroup>
                        <KatLabel text={currencyUnit}/>
                        <NumberKatInput isDisabled={false} className="input-price" minValue={0} isFloat={true}
                                        isMandatory={true}
                                        onInput={(input: number, hasError: boolean): void => {
                                            if (!hasError) {
                                                if (displayStringId === 'SC_FBA_Calc_itemPrice') {
                                                    this.setState({price: input, isValid: true});
                                                } else if (displayStringId === 'SC_FBA_Calc_label_shippingCharge') {
                                                    this.setState({shippingCharge: input, isValid: true});
                                                }
                                            }
                                        }}/>
                    </KatInputGroup>
                </div>
            </div>
        );
    }

    feeCategoriesNotReady() {
        return this.props.feeCatRes.requestStatus !== RequestStatus.SUCCESS;
    }

    hasErrorLoadingFeeCategories() {
        return this.props.feeCatRes.requestStatus === RequestStatus.ERROR;
    }

    render() {
        const {t, programFeesRes, feeCatRes} = this.props;
        const countryCode = this.props.currentCountryCode;
        const dimensionUnitAbbr = this.state.dimensionUnitAbbr;
        let categoryTRexIdMap: Record<number, string>;
        let categoriesMap: Record<string, string> = {};
        if(!this.feeCategoriesNotReady()) {
            categoryTRexIdMap = feeCatRes.feeCategoryListResponse.trexIDToFeeCategoryMap;
            categoriesMap = Object.keys(categoryTRexIdMap).reduce((acc, elem) => {
                acc[t(categoryTRexIdMap[Number(elem)])] = elem;
                return acc;
            }, {} as Record<string, string>);
        }
        return (
            <div className="product-search-content-box">
                <div id="NewAsinEstimationFuncHeader" className="block">
                    <div className="product-search-element"><KatLabel text={t('SC_FBA_Calc_desc_define')}
                                                                      className="product-search-func-header"/></div>
                    <div className="product-search-element"><KatLabel text={t('SC_FBA_Calc_info_define')}
                                                                      className="revenue-calculator-header-content"/>
                    </div>
                </div>

                <form id="NewAsinEstimationInput" onSubmit={this.handleSubmit}>
                    <div className={this.state.hasEstimated ? 'new-asin-input-line' : 'new-asin-input-wrap'}>
                        <KatDropdown richSelectionLabel label={t('SC_FBA_Calc_label_country')} value={countryCode}
                                     className="dropdown-country-new" onChange={this.handleChangeCountry}>
                            {
                                getCountryListConfig().map((country, index) => (
                                    <KatOption value={country} key={index}>
                                        <div className="flexed">
                                            <KatIcon name={("flag-" + country.toLowerCase()) as KatIcon.Name}
                                                     size="small"/>
                                            <span className="paddedMedium">{country}</span>
                                        </div>
                                    </KatOption>
                                ))
                            }
                        </KatDropdown>
                        <KatDropdown label={t('SC_FBA_Calc_label_unit')} value={dimensionUnitAbbr}
                                     className="dropdown-dimension-unit"
                                     onChange={this.handleChangeDimensionUnit}>
                            {
                                getDimensionUnitListConfig().map((dimensionUnit, index) => (
                                    <KatOption value={dimensionUnit} key={index}>{dimensionUnit}</KatOption>
                                ))
                            }
                        </KatDropdown>
                        {this.productDimensions(dimensionUnitAbbr)}
                        {this.productWeight()}
                        <KatDropdown label={t('SC_FBA_Calc_productInfo_category')} value={this.state.tRexId + ''}
                                     className={this.state.hasEstimated ? 'dropdown-category' : 'dropdown-category-wide'}
                                     onChange={this.handleChangeCategory}>
                            <KatOption value={"-1"}>{t('SC_FBA_Calc_option_selectCategory')}</KatOption>
                            {!this.feeCategoriesNotReady()?
                                Object.keys(categoriesMap).sort().map((category, index) => (
                                    <KatOption value={categoriesMap[category]} key={index}>{category}</KatOption>
                                )):<></>
                            }
                            <KatOption
                                value={countryDefaultCategoryTRexIdMap[countryCode] + ""}>{t('FS-ProductCategory-default-FullName')}</KatOption>
                        </KatDropdown>
                        {this.productPrice('SC_FBA_Calc_itemPrice')}
                        {this.productPrice('SC_FBA_Calc_label_shippingCharge')}
                        <KatButton label={t(this.state.hasEstimated ? 'SC_FBA_Calc_btn_update' : 'SC_FBA_Msg_Estimate')} variant="primary"
                                   type="submit" disabled={programFeesRes.requestStatus === RequestStatus.LOADING}
                                   className="button-search"/>
                    </div>
                </form>
                {this.feeCategoriesNotReady() ?
                    <div className="define-new-form-under-loading">
                        <LoadingStatusComponent/>
                    </div> : <></>
                }
                {this.hasErrorLoadingFeeCategories() ?
                    <ErrorStatusComponent description={t('SC_FBA_Calc_calculateFeeFailedWarning')}/> : <></>
                }
                <div>{!this.state.isValid && <ErrorStatusComponent description={t('SC_FBA_Calc_inputValidationFailedWarning')}/>}</div>
            </div>
        );
    }
}

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(NewAsinEstimationComponent));
